import { motion } from "framer-motion"
import { Cookie, Settings, Eye, BarChart3, Target, Globe, MailIcon } from "lucide-react"
import React, { useState, useCallback, useEffect } from "react";
import { HomepageLoader } from "../components/Homepage/HomepageLoader";
import SeoHead from "../seo/SeoHead";
import { generateMetadata } from "../seo/metadataGenerator";
import { PAGE_OG_IMAGES, SITE_URL } from "../seo/constants";
import { generateBreadcrumbSchema, generateGraphSchema, generateWebPageSchema } from "../seo/schemaGenerator";
import { usePrerenderReady } from "../hooks/usePrerenderReady";

export default function CookiePolicyPage() {
    usePrerenderReady();
    const [cursorPosition, setCursorPosition] = useState({ x: -100, y: -100 });
    const [isHovering, setIsHovering] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const updateCursorPosition = useCallback((e) => {
        requestAnimationFrame(() => {
            setCursorPosition({ x: e.clientX, y: e.clientY });
        });
    }, []);

    useEffect(() => {
        document.body.style.cursor = "none";
        const handleMouseEnter = () => setIsHovering(true);
        const handleMouseLeave = () => setIsHovering(false);

        const interactiveElements = document.querySelectorAll(
            'a, button, input, [role="button"]'
        );
        interactiveElements.forEach((element) => {
            element.addEventListener("mouseenter", handleMouseEnter);
            element.addEventListener("mouseleave", handleMouseLeave);
        });

        window.addEventListener("mousemove", updateCursorPosition);

        // Simulate loading time (remove this in production and use real loading logic)
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 3000);

        return () => {
            clearTimeout(timer);
            document.body.style.cursor = "auto";
            window.removeEventListener("mousemove", updateCursorPosition);
            interactiveElements.forEach((element) => {
                element.removeEventListener("mouseenter", handleMouseEnter);
                element.removeEventListener("mouseleave", handleMouseLeave);
            });
        };
    }, [updateCursorPosition]);
    const metadata = generateMetadata({
        title: "Cookie Policy | UpNComer",
        description: "Learn how UpNComer uses cookies and similar technologies to operate and improve your experience.",
        image: PAGE_OG_IMAGES.helpCenter,
        path: "/cookie-policy",
    });

    const webPageSchema = generateWebPageSchema({
        title: "Cookie Policy | UpNComer",
        description: "Learn how UpNComer uses cookies and similar technologies to operate and improve your experience.",
        url: `${SITE_URL}/cookie-policy`,
    });

    const schemas = generateGraphSchema([
        webPageSchema,
        generateBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Cookie Policy", path: "/cookie-policy" },
        ]),
    ]);

    return (
        <>
            <SeoHead metadata={metadata} schemas={schemas} />
            <div
                className={`cursor-spotlight ${isHovering ? "hovering" : ""}`}
                style={{
                    left: `${cursorPosition.x}px`,
                    top: `${cursorPosition.y}px`,
                    transform: `translate(-50%, -50%)`,
                }}
            >
                <div className="inner-circle"></div>
            </div>
            <div
                className={`transition-all duration-1000 ${isLoading ? "opacity-0 blur-xl" : "opacity-100 blur-none"
                    }`}
            >
                <div className="relative min-h-screen bg-gradient-to-br from-[#000456] via-[#00067E] via-[#0a1a8a] to-[#1e3a8a] overflow-hidden">
                    {/* Enhanced Multi-layered Background */}
                    <div className="absolute inset-0 z-0">
                        <div className="absolute inset-0 bg-gradient-to-br from-[#000322] via-[#00067E] via-[#0a1a8a] via-[#1e3a8a] to-[#3b82f6] opacity-90" />

                        {/* Floating orbs */}
                        <motion.div
                            animate={{
                                scale: [1, 1.2, 1],
                                opacity: [0.3, 0.6, 0.3],
                            }}
                            transition={{
                                duration: 10,
                                repeat: Number.POSITIVE_INFINITY,
                                ease: "easeInOut",
                            }}
                            className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-[#6366f1]/20 via-[#4f46e5]/15 to-[#FA6138]/10 rounded-full blur-3xl"
                        />
                    </div>

                    <main className="relative z-10 px-6 md:px-8 pt-16 pb-20">
                        <div className="max-w-4xl mx-auto">
                            {/* Header */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8 }}
                                className="text-center mb-16"
                            >
                                <div className="flex justify-center mb-6">
                                    <div className="p-4 bg-[#FA6138]/20 rounded-2xl">
                                        <Cookie className="w-12 h-12 text-[#FA6138]" />
                                    </div>
                                </div>
                                <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-[#c7d2fe] via-[#a5b4fc] to-[#818cf8] bg-clip-text text-transparent mb-6 drop-shadow-lg">
                                    Cookie Policy
                                </h1>
                                <p className="bg-gradient-to-r from-[#818cf8]/80 via-[#6366f1]/85 to-[#4f46e5]/80 bg-clip-text text-transparent text-xl font-light max-w-3xl mx-auto leading-relaxed">
                                    Learn how we use essential technologies to operate and improve your UpNComer experience.
                                </p>
                                <div className="text-[#818cf8]/60 text-sm mt-4">Last updated: November 23, 2025</div>
                            </motion.div>

                            {/* Content */}
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                                className="bg-gradient-to-br from-[#312e81]/20 via-[#1e1b4b]/15 to-[#0f0f23]/25 backdrop-blur-xl border border-[#6366f1]/20 rounded-2xl p-8 md:p-12"
                                style={{
                                    boxShadow: "0 20px 40px rgba(99, 102, 241, 0.25), inset 0 1px 0 rgba(165, 180, 252, 0.15)",
                                }}
                            >
                                <div className="prose prose-invert max-w-none">
                                    <div className="space-y-8">
                                        {/* What Are Cookies */}
                                        <section>
                                            <div className="flex items-center mb-4">
                                                <Cookie className="w-6 h-6 text-[#FA6138] mr-3" />
                                                <h2 className="text-2xl font-bold text-[#c7d2fe]">What Are Cookies and Similar Technologies?</h2>
                                            </div>
                                            <p className="text-[#818cf8]/80 leading-relaxed">
                                                “Cookies” and similar tools (such as local storage, pixels, or analytics tags) are small data elements that help us run the UpNComer platform securely and efficiently.
                                                They remember your login session, preferences, and activity to make sure the Service works the way you expect.
                                                UpNComer uses these technologies only for functional and analytical purposes—never for advertising, tracking across other websites, or selling data.
                                            </p>
                                        </section>

                                        {/* How We Use Cookies */}
                                        <section>
                                            <div className="flex items-center mb-4">
                                                <Settings className="w-6 h-6 text-[#FA6138] mr-3" />
                                                <h2 className="text-2xl font-bold text-[#c7d2fe]">How We Use Them</h2>
                                            </div>
                                            <p className="text-[#818cf8]/80 leading-relaxed mb-4">
                                                We use cookies and local storage to:
                                            </p>
                                            <ul className="list-disc list-inside text-[#818cf8]/80 space-y-2 ml-4">
                                                <li>Keep you signed in and manage secure sessions</li>
                                                <li>Save campaign data and user preferences across modules</li>
                                                <li>Measure basic analytics (e.g., page load times, feature usage, system performance)</li>
                                                <li>Detect bugs, improve reliability, and protect against fraud or abuse</li>
                                            </ul>
                                            <p className="text-[#818cf8]/80 leading-relaxed mt-2">
                                                All data collected is <b>aggregated or anonymized</b> and used strictly to maintain or improve the Service.
                                            </p>
                                        </section>

                                        {/* Types of Cookies */}
                                        <section>
                                            <h2 className="text-2xl font-bold text-[#c7d2fe] mb-6">Types of Cookies & Storage We Use</h2>
                                            <div className="space-y-6">
                                                <div className="bg-[#1e3a8a]/20 rounded-lg p-6 border border-[#3b82f6]/30">
                                                    <div className="flex items-center mb-3">
                                                        <Eye className="w-5 h-5 text-[#FA6138] mr-2" />
                                                        <h3 className="text-lg font-semibold text-[#a5b4fc]">Essential / Functional</h3>
                                                    </div>
                                                    <p className="text-[#818cf8]/80 text-sm leading-relaxed">
                                                        Required for the platform to operate—includes authentication tokens, encryption keys, and preferences.<br />
                                                        <i>(Examples: Redux Persist, session identifiers, Crypto.js-encrypted Growth Engine data, and login tokens.)</i>
                                                    </p>
                                                    {/* <div className="mt-3">
                                                        <span className="text-xs bg-[#FA6138]/20 text-[#FA6138] px-2 py-1 rounded">Required</span>
                                                    </div> */}
                                                </div>

                                                <div className="bg-[#1e3a8a]/20 rounded-lg p-6 border border-[#3b82f6]/30">
                                                    <div className="flex items-center mb-3">
                                                        <BarChart3 className="w-5 h-5 text-[#FA6138] mr-2" />
                                                        <h3 className="text-lg font-semibold text-[#a5b4fc]">Analytics</h3>
                                                    </div>
                                                    <p className="text-[#818cf8]/80 text-sm leading-relaxed">
                                                        Used internally to understand how artists interact with UpNComer’s tools so we can improve them.
                                                        (Examples: Amplitude, PostHog, Google Analytics basic usage tracking.)
                                                        These do not track users across other websites or show targeted ads.
                                                    </p>
                                                    {/* <div className="mt-3">
                                                        <span className="text-xs bg-[#3b82f6]/20 text-[#93c5fd] px-2 py-1 rounded">Optional</span>
                                                    </div> */}
                                                </div>

                                                <div className="bg-[#1e3a8a]/20 rounded-lg p-6 border border-[#3b82f6]/30">
                                                    <div className="flex items-center mb-3">
                                                        <Settings className="w-5 h-5 text-[#FA6138] mr-2" />
                                                        <h3 className="text-lg font-semibold text-[#a5b4fc]">No Advertising or Third-Party Tracking</h3>
                                                    </div>
                                                    <p className="text-[#818cf8]/80 leading-relaxed mb-4">
                                                        UpNComer does not use cookies for:
                                                    </p>
                                                    <ul className="list-disc list-inside text-[#818cf8]/80 space-y-2 ml-4">
                                                        <li>Retargeting or personalized advertising</li>
                                                        <li>Third-party ad networks</li>
                                                        <li>Selling or renting data to marketers</li>
                                                    </ul>
                                                    <p className="text-[#818cf8]/80 leading-relaxed mt-2">
                                                        Any integrations with partners (e.g., Meta, TikTok, YouTube) use <b>API-based connections</b> that only activate when you deliberately link your account.
                                                    </p>
                                                    {/* <div className="mt-3">
                                                        <span className="text-xs bg-[#3b82f6]/20 text-[#93c5fd] px-2 py-1 rounded">Optional</span>
                                                    </div> */}
                                                </div>

                                                <div className="bg-[#1e3a8a]/20 rounded-lg p-6 border border-[#3b82f6]/30">
                                                    <div className="flex items-center mb-3">
                                                        <Target className="w-5 h-5 text-[#FA6138] mr-2" />
                                                        <h3 className="text-lg font-semibold text-[#a5b4fc]">Managing or Clearing Cookies</h3>
                                                    </div>
                                                    <p className="text-[#818cf8]/80 text-sm leading-relaxed">
                                                        You can clear stored data anytime in your browser settings.<br />
                                                        If you disable functional cookies, some core features (like login persistence or analytics dashboards) may not work correctly.
                                                    </p>
                                                    {/* <div className="mt-3">
                                                        <span className="text-xs bg-[#3b82f6]/20 text-[#93c5fd] px-2 py-1 rounded">Optional</span>
                                                    </div> */}
                                                </div>
                                            </div>
                                        </section>

                                        {/* Updates to Policy */}
                                        <section>
                                            <h2 className="text-2xl font-bold text-[#c7d2fe] mb-4">Updates to This Policy</h2>
                                            <p className="text-[#818cf8]/80 leading-relaxed">
                                                We may revise this Cookie Policy occasionally to reflect new technologies or analytics tools.<br />
                                                If major changes occur, we’ll post an updated notice on this page with a new “Last updated” date.
                                            </p>
                                        </section>

                                        {/* Contact Information */}
                                        <section>
                                            <h2 className="text-2xl font-bold text-[#c7d2fe] mb-4">Contact Us</h2>
                                            <p className="text-[#818cf8]/80 leading-relaxed mb-4">
                                                For questions about cookies or data storage, contact our Data Protection Officer:
                                            </p>
                                            <div className="bg-[#1e3a8a]/20 rounded-lg p-4 border border-[#3b82f6]/30">
                                                <p className="text-[#c7d2fe] mb-2">
                                                    <strong>UpNComer Pte. Ltd.</strong>
                                                </p>
                                                <p className="text-[#818cf8]/80">6 Marina Boulevard, 11-18, Singapore 018985</p>
                                                <p className="text-[#818cf8]/80">
                                                    <MailIcon />
                                                    <a href="mailto:support@upncomer.co" className="text-[#FA6138] hover:underline">
                                                        support@upncomer.co
                                                    </a>
                                                </p>
                                            </div>
                                        </section>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </main>
                </div>
            </div>
        </>
    )
}
